import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Edit, Globe, Calendar, DollarSign, TrendingUp, Award,
  Clock, User, FileText, Users, Building2, Mail, Phone, Plus, X,
  ExternalLink, Trash2, RotateCcw, CreditCard
} from 'lucide-react';

const PROJECT_STATUSES = [
  { id: 'not_started', label: 'Not Started', color: 'bg-gray-100 text-gray-700' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { id: 'ongoing', label: 'Ongoing', color: 'bg-[#0A1628] text-white' },
  { id: 'on_hold', label: 'On Hold', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { id: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
];

const PROJECT_TYPES = [
  'Web Design', 'DME Setup', 'CRM Implementation', 'Consulting', 'Training', 
  'System Integration', 'Custom Development', 'Support Contract', 'Other'
];

const COST_CATEGORIES = [
  { id: 'hosting', label: 'Hosting' },
  { id: 'api_service', label: 'API / Service' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'credits', label: 'Credits' },
  { id: 'messaging', label: 'Messaging (SMS/Email)' },
  { id: 'other', label: 'Other' },
];

const COST_FREQUENCIES = [
  { id: 'one_time', label: 'One-Time' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
];

const TabButton = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active ? 'border-[#014DB7] text-[#014DB7]' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    {label}
  </button>
);

const TagList = ({ items, color = 'blue' }) => {
  if (!items?.length) return <span className="text-sm text-gray-400">None</span>;
  const colorClasses = color === 'green' ? 'bg-[#84CC16]/10 text-[#65a30d]' : 'bg-[#014DB7]/10 text-[#014DB7]';
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, idx) => (
        <span key={idx} className={`text-xs px-2 py-1 rounded-full ${colorClasses}`}>{item}</span>
      ))}
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, iconBg = 'bg-gray-100', iconColor = 'text-gray-600' }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="font-semibold text-[#0A1628]">{value || 'Not set'}</p>
      </div>
    </div>
  </div>
);

const CostSummaryCard = ({ label, value, suffix = '', dark = false }) => (
  <div className={`rounded-xl p-4 ${dark ? 'bg-[#0A1628] text-white' : 'bg-white border border-gray-200'}`}>
    <p className={`text-xs mb-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
    <p className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#0A1628]'}`}>
      ${value.toLocaleString()}{suffix}
    </p>
  </div>
);

const AddCostModal = ({ isOpen, onClose, onSave, projectId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    category: 'other',
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        amount: '',
        frequency: 'monthly',
        category: 'other',
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    onSave({
      ...formData,
      project_id: projectId,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#0A1628]">Add Project Cost</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>Cost Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., AWS Hosting, OpenAI API, Twilio SMS"
              className="rounded-xl"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description..."
              rows={2}
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Frequency *</Label>
              <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COST_FREQUENCIES.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COST_CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              Add Cost
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [costs, setCosts] = useState([]);
  const [costTotals, setCostTotals] = useState({ one_time: 0, monthly: 0, yearly: 0, est_yearly_total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({});
  const [costModalOpen, setCostModalOpen] = useState(false);

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'sales';

  const fetchCosts = async () => {
    try {
      const res = await api.get(`/api/project-costs?project_id=${id}`);
      setCosts(res.data.costs);
      setCostTotals(res.data.totals);
    } catch (error) {
      console.error('Failed to load costs');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectRes, usersRes, tasksRes, costsRes] = await Promise.all([
          api.get(`/api/projects/${id}`),
          api.get('/api/users'),
          api.get(`/api/tasks?project_id=${id}`),
          api.get(`/api/project-costs?project_id=${id}`),
        ]);
        setProject(projectRes.data);
        setFormData(projectRes.data);
        setUsers(usersRes.data);
        setTasks(tasksRes.data);
        setCosts(costsRes.data.costs);
        setCostTotals(costsRes.data.totals);
      } catch (error) {
        toast.error('Failed to load project');
        navigate('/crm/projects');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSave = async () => {
    try {
      await api.put(`/api/projects/${id}`, formData);
      setProject(formData);
      setEditing(false);
      toast.success('Project updated');
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleQuickStatusChange = async (newStatus) => {
    try {
      await api.put(`/api/projects/${id}`, { status: newStatus });
      setProject(prev => ({ ...prev, status: newStatus }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddCost = async (costData) => {
    try {
      await api.post('/api/project-costs', costData);
      toast.success('Cost added');
      setCostModalOpen(false);
      fetchCosts();
    } catch (error) {
      toast.error('Failed to add cost');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Delete this cost?')) return;
    try {
      await api.delete(`/api/project-costs/${costId}`);
      toast.success('Cost deleted');
      fetchCosts();
    } catch (error) {
      toast.error('Failed to delete cost');
    }
  };

  const getStatusInfo = (status) => PROJECT_STATUSES.find(s => s.id === status) || PROJECT_STATUSES[2];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.id === priority) || PRIORITIES[1];
  const getAssignedUsers = () => users.filter(u => project?.assigned_to?.includes(u.id));
  const getCategoryLabel = (cat) => COST_CATEGORIES.find(c => c.id === cat)?.label || cat;
  const getFrequencyLabel = (freq) => COST_FREQUENCIES.find(f => f.id === freq)?.label || freq;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const statusInfo = getStatusInfo(project.status);
  const priorityInfo = getPriorityInfo(project.priority);
  const assignedUsers = getAssignedUsers();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatBudget = (budget) => {
    if (!budget) return 'Not set';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budget);
  };

  return (
    <div className="space-y-6 -m-6 p-6 bg-[#F8FAFC] min-h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link to="/crm/projects" className="mt-1 p-2 text-gray-400 hover:text-[#014DB7] rounded-lg hover:bg-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-[#0A1628]">{project.name}</h1>
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            </div>
            <p className="text-gray-500">
              {project.client_name || 'No client'} • {project.project_type || 'General'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {project.website_url && (
            <Button variant="outline" className="rounded-xl border-[#84CC16] text-[#84CC16] hover:bg-[#84CC16]/10" asChild>
              <a href={project.website_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" /> See Development Site
              </a>
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => setEditing(true)} variant="outline" className="rounded-xl">
              <Edit className="w-4 h-4 mr-2" /> Edit Project
            </Button>
          )}
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard icon={Clock} label="Due Date" value={formatDate(project.due_date)} iconBg="bg-blue-50" iconColor="text-blue-500" />
        <MetricCard icon={DollarSign} label="Budget" value={formatBudget(project.budget)} iconBg="bg-green-50" iconColor="text-green-500" />
        <MetricCard icon={Award} label="Domain Authority" value="N/A" iconBg="bg-purple-50" iconColor="text-purple-500" />
        <MetricCard icon={TrendingUp} label="Organic Traffic" value="N/A" iconBg="bg-orange-50" iconColor="text-orange-500" />
      </div>

      {/* Quick Status Update */}
      <div className="bg-white rounded-xl p-4 border border-gray-200">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 font-medium">Quick Status Update:</span>
          <div className="flex flex-wrap gap-2">
            {PROJECT_STATUSES.map(status => (
              <button
                key={status.id}
                onClick={() => handleQuickStatusChange(status.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  project.status === status.id
                    ? status.color
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-t-xl border border-gray-200 border-b-0">
        <div className="flex items-center gap-1 px-4 border-b border-gray-200 overflow-x-auto">
          <TabButton active={activeTab === 'overview'} label="Overview" onClick={() => setActiveTab('overview')} />
          <TabButton active={activeTab === 'tasks'} label="Tasks" onClick={() => setActiveTab('tasks')} />
          <TabButton active={activeTab === 'costs'} label="Costs" onClick={() => setActiveTab('costs')} />
          <TabButton active={activeTab === 'invoices'} label="Invoices" onClick={() => setActiveTab('invoices')} />
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-b-xl border border-gray-200 border-t-0 p-6 -mt-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Project Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-4">Project Details</h3>
                <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Description</p>
                    <p className="text-[#0A1628]">{project.description || 'No description'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Project Type</p>
                      <p className="font-medium text-[#0A1628]">{project.project_type || 'General'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Priority</p>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${priorityInfo.color}`}>
                        {priorityInfo.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {project.interests?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-4">Features Interested In</h3>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <TagList items={project.interests} color="blue" />
                  </div>
                </div>
              )}

              {project.licensed_states?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-4">Licensed States</h3>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <TagList items={project.licensed_states} color="blue" />
                  </div>
                </div>
              )}

              {project.dme_categories?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-4">DME Categories</h3>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <TagList items={project.dme_categories} color="green" />
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Client & Team */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-4">Client Information</h3>
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#014DB7] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {project.client_name?.[0] || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A1628]">{project.client_name || 'No client assigned'}</p>
                      <p className="text-sm text-gray-500">{project.company || 'No company'}</p>
                    </div>
                  </div>
                  
                  {project.client_email && (
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{project.client_email}</span>
                    </div>
                  )}
                  {project.client_phone && (
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{project.client_phone}</span>
                    </div>
                  )}
                  
                  {project.lead_id && (
                    <Link 
                      to={`/crm/leads/${project.lead_id}`} 
                      className="inline-flex items-center gap-2 mt-3 text-sm text-[#014DB7] hover:underline"
                    >
                      View Original Lead <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-4">Assigned Team</h3>
                <div className="bg-gray-50 rounded-xl p-5">
                  {assignedUsers.length > 0 ? (
                    <div className="space-y-3">
                      {assignedUsers.map(user => (
                        <div key={user.id} className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#84CC16] rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">{user.first_name?.[0]}{user.last_name?.[0]}</span>
                          </div>
                          <div>
                            <p className="font-medium text-[#0A1628]">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-gray-400">{user.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">No team members assigned</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div>
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div key={task.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        task.status === 'done' ? 'bg-green-500' : 
                        task.status === 'in_progress' ? 'bg-yellow-500' : 'bg-gray-300'
                      }`} />
                      <span className="font-medium text-[#0A1628]">{task.title}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      task.status === 'done' ? 'bg-green-100 text-green-700' :
                      task.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {task.status?.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No tasks for this project yet</p>
                <Link to="/crm/tasks">
                  <Button className="mt-4 bg-[#014DB7] text-white rounded-xl">
                    <Plus className="w-4 h-4 mr-2" /> Add Task
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="space-y-6">
            {/* Cost Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <CostSummaryCard label="One-Time Costs" value={costTotals.one_time} />
              <CostSummaryCard label="Monthly Costs" value={costTotals.monthly} suffix="/mo" />
              <CostSummaryCard label="Yearly Costs" value={costTotals.yearly} suffix="/yr" />
              <CostSummaryCard label="Est. Yearly Total" value={costTotals.est_yearly_total} dark />
            </div>

            {/* Project Running Costs */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0A1628]">Project Running Costs</h3>
                <Button onClick={() => setCostModalOpen(true)} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
                  <Plus className="w-4 h-4 mr-2" /> Add Cost
                </Button>
              </div>

              {costs.length > 0 ? (
                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Cost Name</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Category</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Frequency</th>
                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Amount</th>
                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-20"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {costs.map(cost => (
                        <tr key={cost.id} className="hover:bg-gray-100 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-[#0A1628]">{cost.name}</p>
                              {cost.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{cost.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{getCategoryLabel(cost.category)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              cost.frequency === 'monthly' ? 'bg-blue-100 text-blue-700' :
                              cost.frequency === 'yearly' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {getFrequencyLabel(cost.frequency)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-[#0A1628]">
                              ${cost.amount.toLocaleString()}
                              {cost.frequency === 'monthly' && '/mo'}
                              {cost.frequency === 'yearly' && '/yr'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button 
                              onClick={() => handleDeleteCost(cost.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 font-medium mb-1">No costs added yet</p>
                  <p className="text-sm text-gray-400 mb-4">Add running costs like hosting, API subscriptions, and more</p>
                  <Button onClick={() => setCostModalOpen(true)} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
                    <Plus className="w-4 h-4 mr-2" /> Add First Cost
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No invoices linked to this project</p>
            <Link to="/crm/invoices">
              <Button className="mt-4 bg-[#014DB7] text-white rounded-xl">
                <Plus className="w-4 h-4 mr-2" /> Create Invoice
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <EditProjectModal
          project={project}
          formData={formData}
          setFormData={setFormData}
          users={users}
          onSave={handleSave}
          onClose={() => { setEditing(false); setFormData(project); }}
        />
      )}

      {/* Add Cost Modal */}
      <AddCostModal
        isOpen={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        onSave={handleAddCost}
        projectId={id}
      />
    </div>
  );
};

const EditProjectModal = ({ project, formData, setFormData, users, onSave, onClose }) => {
  const toggleAssignee = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: (prev.assigned_to || []).includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...(prev.assigned_to || []), userId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A1628]">Edit Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Project Type</Label>
              <Select value={formData.project_type || ''} onValueChange={(v) => setFormData({ ...formData, project_type: v })}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map(type => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="rounded-xl" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={formData.client_name || ''} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Budget ($)</Label>
              <Input type="number" value={formData.budget || ''} onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || null })} className="rounded-xl" placeholder="0.00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={formData.start_date || ''} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={formData.due_date || ''} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="rounded-xl" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign Team Members</Label>
            <div className="flex flex-wrap gap-2">
              {users.map(user => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleAssignee(user.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    (formData.assigned_to || []).includes(user.id)
                      ? 'bg-[#014DB7] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {user.first_name} {user.last_name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button onClick={onSave} className="bg-[#014DB7] text-white rounded-xl">Save Changes</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
